import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

import API from '../config';
import { Helmet } from 'react-helmet-async';
// ── OTP Verify  ─────────────────────────────────────────────────────
const OTPVerify = ({ email, onDone, onResend, T }) => {
  const [otp, setOtp]             = useState(['', '', '', '', '', '']);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown]   = useState(0);
  const inputs                    = React.useRef([]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    setError('');
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) { setOtp(paste.split('')); inputs.current[5]?.focus(); }
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { setError('Enter the full 6-digit code'); return; }
    setLoading(true);
    try {
      await onDone(code);
    } catch (err) {
      setError(err.message || 'Verification failed. Try again.');
      setOtp(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    const result = await onResend();
    if (result?.success) {
      setCooldown(60);
      setOtp(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } else {
      setError(result?.error || 'Failed to resend. Try again.');
    }
    setResending(false);
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 4)) + c)
    : 'your email';

  return (
    <div style={{ width: '100%', maxWidth: 420 }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${T.accent}1a`, border: `1px solid ${T.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24 }}>✉️</div>
        <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Verify your email</h1>
        <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6 }}>
          We sent a 6-digit code to<br />
          <span style={{ color: T.text, fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>{maskedEmail}</span>
        </p>
      </div>
      {error && <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13, textAlign: 'center' }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 28 }}>
          {otp.map((digit, i) => (
            <input key={i} ref={el => inputs.current[i] = el}
              type="text" inputMode="numeric" maxLength={1} value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onPaste={handlePaste}
              autoFocus={i === 0}
              style={{ width: 52, height: 58, textAlign: 'center', background: T.surface, border: `1px solid ${digit ? T.accent : T.border}`, borderRadius: 10, color: T.text, fontSize: 24, fontWeight: 700, fontFamily: "'DM Mono', monospace", outline: 'none', transition: 'border-color 0.15s' }} />
          ))}
        </div>
        <button type="submit" disabled={loading || otp.join('').length !== 6}
          style={{ width: '100%', background: otp.join('').length !== 6 ? T.surfaceHi : T.accent, color: otp.join('').length !== 6 ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: otp.join('').length !== 6 ? 'not-allowed' : 'pointer', fontSize: 14, marginBottom: 16, transition: 'all 0.15s' }}>
          {loading ? 'Verifying...' : 'Verify email →'}
        </button>
      </form>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: T.textMuted, fontSize: 13 }}>
          Didn't receive the code?{' '}
          {cooldown > 0
            ? <span style={{ color: T.textDim, fontFamily: "'DM Mono', monospace" }}>Resend in {cooldown}s</span>
            : <span onClick={!resending ? handleResend : undefined} style={{ color: resending ? T.textDim : T.accent, cursor: resending ? 'default' : 'pointer', fontWeight: 600 }}>
                {resending ? 'Sending...' : 'Resend code'}
              </span>
          }
        </p>
      </div>
    </div>
  );
};

// ── Nickname Picker ───────────────────────────────────────────────────────────
const NicknamePicker = ({ token, onDone, T }) => {
  const [username, setUsername]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [checking, setChecking]   = useState(false);
  const [available, setAvailable] = useState(null);
  const { updateUser }            = useAuth();

  const randomSuggestion = () => {
    const adj  = ['sharp', 'bold', 'quick', 'smart', 'cool', 'top'];
    const noun = ['predictor', 'oracle', 'prophet', 'analyst', 'pundit', 'sage'];
    return `${adj[Math.floor(Math.random() * adj.length)]}_${noun[Math.floor(Math.random() * noun.length)]}${Math.floor(Math.random() * 999)}`;
  };
  const [suggestions] = useState(() => [randomSuggestion(), randomSuggestion(), randomSuggestion()]);

  const handleCheck = async (val) => {
    const cleaned = val.trim().toLowerCase().replace(/\s+/g, '_');
    setUsername(cleaned);
    setAvailable(null);
    if (cleaned.length < 3) return;
    setChecking(true);
    try {
      const res = await axios.post(`${API}/api/auth/check-username`, { username: cleaned });
      setAvailable(res.data.available);
    } catch (_) {}
    finally { setChecking(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/set-username`, { username }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateUser({ username });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set username');
    } finally { setLoading(false); }
  };

  const borderColor = username.length >= 3
    ? available === true ? T.yes : available === false ? T.no : T.border
    : T.border;

  return (
    <div style={{ width: '100%', maxWidth: 420 }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Pick your nickname</h1>
        <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6 }}>This is how other predictors will see you on Mino.</p>
      </div>

      {error && <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13 }}>{error}</div>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Username</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMuted, fontSize: 14, pointerEvents: 'none' }}>@</span>
            <input type="text" value={username} onChange={e => handleCheck(e.target.value)} placeholder="your_nickname" required maxLength={30}
              style={{ width: '100%', background: T.surface, border: `1px solid ${borderColor}`, borderRadius: 8, padding: '12px 40px 12px 28px', color: T.text, fontSize: 14, fontFamily: 'inherit', transition: 'border-color 0.2s', outline: 'none' }} />
            <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12 }}>
              {checking ? <span style={{ color: T.textMuted }}>...</span>
                : username.length >= 3 && available === true  ? <span style={{ color: T.yes }}>✓</span>
                : username.length >= 3 && available === false ? <span style={{ color: T.no }}>✗</span>
                : null}
            </span>
          </div>
          <p style={{ color: T.textDim, fontSize: 11, marginTop: 6 }}>Letters, numbers, underscores · 3–30 characters</p>
        </div>

        <div>
          <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Suggestions</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => handleCheck(s)}
                style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 10px', color: T.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: "'DM Mono', monospace", transition: 'all 0.1s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.text; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}>
                @{s}
              </button>
            ))}
          </div>
        </div>

        <button type="submit" disabled={loading || !username || available === false}
          style={{ background: loading || !username || available === false ? T.surfaceHi : T.accent, color: loading || !username || available === false ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: loading || !username || available === false ? 'not-allowed' : 'pointer', fontSize: 14, transition: 'all 0.15s' }}>
          {loading ? 'Setting nickname...' : "Let's go →"}
        </button>
        <button type="button" onClick={onDone}
          style={{ background: 'transparent', border: 'none', color: T.textDim, fontSize: 13, cursor: 'pointer', padding: 4 }}>
          Skip for now
        </button>
      </form>
    </div>
  );
};

// ── Forgot Password  ────────────────────────────────────────────
const ForgotPassword = ({ onBack, T }) => {
  const [step, setStep]                       = useState('email');
  const [email, setEmail]                     = useState('');
  const [otp, setOtp]                         = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass]               = useState(false);
  const [error, setError]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [cooldown, setCooldown]               = useState(0);
  const [resetToken, setResetToken]           = useState('');
  const inputs                                = React.useRef([]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/users/reset-password/request`, { email });
      setStep('otp');
      setCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send code. Try again.');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    setError('');
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };
  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) { setOtp(paste.split('')); inputs.current[5]?.focus(); }
    e.preventDefault();
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { setError('Enter the full 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/users/reset-password/verify`, { email, otp: code });
      setResetToken(res.data.reset_token);
      setStep('newpass');
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed.');
      setOtp(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/users/reset-password/request`, { email });
      setCooldown(60);
      setOtp(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend.');
    } finally { setLoading(false); }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(newPassword)) { setError('Password must contain at least one uppercase letter'); return; }
    if (!/[0-9]/.test(newPassword)) { setError('Password must contain at least one number'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/users/reset-password/confirm`, {
        reset_token: resetToken,
        new_password: newPassword,
      });
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally { setLoading(false); }
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 4)) + c)
    : '';

  const strength = (() => {
    const p = newPassword;
    if (!p) return null;
    if (p.length < 6) return { label: 'Too short', color: T.no, w: '25%' };
    if (p.length < 8) return { label: 'Weak', color: T.no, w: '40%' };
    if (!/[A-Z]/.test(p) || !/[0-9]/.test(p)) return { label: 'Fair', color: T.gold || '#f3a94a', w: '65%' };
    return { label: 'Strong', color: T.yes, w: '100%' };
  })();

  const inputStyle = { width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' };

  return (
    <div style={{ width: '100%', maxWidth: 400 }}>
      {step === 'email' && (
        <div>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 24 }}>← Back to sign in</button>
          <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Reset password</h1>
          <p style={{ color: T.textMuted, fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>Enter your registered email and we'll send you a verification code.</p>
          {error && <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 16, fontSize: 13 }}>{error}</div>}
          <form onSubmit={handleRequestReset} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Email Address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={inputStyle} />
            </div>
            <button type="submit" disabled={loading || !email}
              style={{ background: loading || !email ? T.surfaceHi : T.accent, color: loading || !email ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: loading || !email ? 'not-allowed' : 'pointer', fontSize: 14, transition: 'all 0.15s' }}>
              {loading ? 'Sending code...' : 'Send reset code →'}
            </button>
          </form>
        </div>
      )}

      {step === 'otp' && (
        <div>
          <button onClick={() => setStep('email')} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 24 }}>← Back</button>
          <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Enter reset code</h1>
          <p style={{ color: T.textMuted, fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
            We sent a 6-digit code to <span style={{ color: T.text, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>{maskedEmail}</span>
          </p>
          {error && <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 16, fontSize: 13, textAlign: 'center' }}>{error}</div>}
          <form onSubmit={handleVerifyOTP}>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 24 }}>
              {otp.map((digit, i) => (
                <input key={i} ref={el => inputs.current[i] = el}
                  type="text" inputMode="numeric" maxLength={1} value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  onPaste={handleOtpPaste}
                  autoFocus={i === 0}
                  style={{ width: 48, height: 54, textAlign: 'center', background: T.surface, border: `1px solid ${digit ? T.accent : T.border}`, borderRadius: 10, color: T.text, fontSize: 22, fontWeight: 700, fontFamily: "'DM Mono',monospace", outline: 'none', transition: 'border-color 0.15s' }} />
              ))}
            </div>
            <button type="submit" disabled={loading || otp.join('').length !== 6}
              style={{ width: '100%', background: otp.join('').length !== 6 ? T.surfaceHi : T.accent, color: otp.join('').length !== 6 ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: otp.join('').length !== 6 ? 'not-allowed' : 'pointer', fontSize: 14, marginBottom: 16, transition: 'all 0.15s' }}>
              {loading ? 'Verifying...' : 'Verify code →'}
            </button>
          </form>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: T.textMuted, fontSize: 13 }}>
              Didn't receive the code?{' '}
              {cooldown > 0
                ? <span style={{ color: T.textDim, fontFamily: "'DM Mono',monospace" }}>Resend in {cooldown}s</span>
                : <span onClick={!loading ? handleResendOTP : undefined} style={{ color: T.accent, cursor: 'pointer', fontWeight: 600 }}>Resend</span>
              }
            </p>
          </div>
        </div>
      )}

      {step === 'newpass' && (
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Set new password</h1>
          <p style={{ color: T.textMuted, fontSize: 14, marginBottom: 28 }}>Choose a strong password for your account.</p>
          {error && <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 16, fontSize: 13 }}>{error}</div>}
          <form onSubmit={handleSetPassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>New Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} required placeholder="Min. 8 characters"
                  style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 42px 12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
                  {showPass ? 'hide' : 'show'}
                </button>
              </div>
              {strength && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ height: 3, background: T.surfaceHi, borderRadius: 2, overflow: 'hidden', marginBottom: 5 }}>
                    <div style={{ height: '100%', width: strength.w, background: strength.color, borderRadius: 2, transition: 'all 0.3s ease' }} />
                  </div>
                  <span style={{ fontSize: 11, color: strength.color, fontFamily: "'DM Mono',monospace" }}>{strength.label}</span>
                </div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Confirm Password</label>
              <input type={showPass ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required placeholder="Repeat password"
                style={{ width: '100%', background: T.surface, border: `1px solid ${confirmPassword && confirmPassword !== newPassword ? T.no : T.border}`, borderRadius: 8, padding: '12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
            </div>
            <button type="submit" disabled={loading || !newPassword || !confirmPassword}
              style={{ background: loading || !newPassword || !confirmPassword ? T.surfaceHi : T.accent, color: loading || !newPassword || !confirmPassword ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: loading || !newPassword || !confirmPassword ? 'not-allowed' : 'pointer', fontSize: 14, marginTop: 4, transition: 'all 0.15s' }}>
              {loading ? 'Updating...' : 'Update password →'}
            </button>
          </form>
        </div>
      )}

      {step === 'done' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${T.yes}1a`, border: `1px solid ${T.yes}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28 }}>✓</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Password updated!</h1>
          <p style={{ color: T.textMuted, fontSize: 14, marginBottom: 28 }}>Your password has been changed. You can now sign in.</p>
          <button onClick={onBack} style={{ background: T.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '13px 32px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            Sign in →
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Login Page ───────────────────────────────────────────────────────────
export default function Login() {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [showPass, setShowPass]   = useState(false);
  const [step, setStep]           = useState('login'); // login | verify | forgot | nickname
  const [authToken, setAuthToken] = useState(null);

  const { login, updateUser }     = useAuth();
  const { T }                     = useTheme();
 
  const navigate                  = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email, password });

      if (res.data.requires_verification) {
  setAuthToken(res.data.token);
  login(res.data.user, res.data.token);
  setStep('verify');
  return;
}

      login(res.data.user, res.data.token);
      if (!res.data.user.username) {
        setAuthToken(res.data.token);
        setStep('nickname');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  // Firebase OTP confirmed for unverified login → exchange for verified app JWT
  const handleVerified = async (code) => {
  try {
    const res = await axios.post(`${API}/api/auth/verify-otp`, { otp: code }, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    login(res.data.user, res.data.token);
    setAuthToken(res.data.token);
    if (!res.data.user.username) {
      setStep('nickname');
    } else {
      navigate('/dashboard');
    }
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Verification failed');
  }
};

  const handleResend = async () => {
  try {
    await axios.post(`${API}/api/auth/resend-otp`, {}, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to resend' };
  }
};
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: T.text }}>
      <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        input:focus { border-color:${T.accent} !important; outline:none; }
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      
      <nav style={{ height: 56, display: 'flex', alignItems: 'center', padding: '0 24px', borderBottom: `1px solid ${T.border}` }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <svg width="140" height="38" viewBox="0 0 160 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#131416" stroke="#2a2b2f" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
            <text x="46" y="20" dominantBaseline="middle" fontFamily="'DM Sans',sans-serif" fontWeight="800" fontSize="18" fill={T.text} letterSpacing="-1">Mino Markets</text>
          </svg>
        </div>
      </nav>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>

        {step === 'verify' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            <OTPVerify email={email} onDone={handleVerified} onResend={handleResend} T={T} />
          </div>
        )}

        {step === 'nickname' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            <NicknamePicker token={authToken} onDone={() => navigate('/dashboard')} T={T} />
          </div>
        )}

        {step === 'forgot' && (
          <div style={{ width: '100%', maxWidth: 400, animation: 'fadeIn 0.3s ease' }}>
            <ForgotPassword onBack={() => setStep('login')} T={T} />
          </div>
        )}

        {step === 'login' && (
          <div style={{ width: '100%', maxWidth: 400, animation: 'fadeIn 0.3s ease' }}>
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 6 }}>Sign in</h1>
              <p style={{ color: T.textMuted, fontSize: 14 }}>Welcome back to Mino</p>
            </div>

            {error && (
              <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com"
                  style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Password</label>
                  <span onClick={() => setStep('forgot')} style={{ fontSize: 12, color: T.accent, cursor: 'pointer', fontWeight: 500 }}>Forgot password?</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••"
                    style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 42px 12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
                    {showPass ? 'hide' : 'show'}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading}
                style={{ background: loading ? T.surfaceHi : T.accent, color: loading ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 14, marginTop: 4, transition: 'all 0.15s' }}>
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
               <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
  <div style={{ flex: 1, height: 1, background: T.border }} />
  <span style={{ color: T.textDim, fontSize: 12 }}>or</span>
  <div style={{ flex: 1, height: 1, background: T.border }} />
</div>

<button
  type="button"
  onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`}
  style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 13, fontWeight: 600, cursor: 'pointer', fontSize: 14, color: T.text, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'border-color 0.15s' }}
  onMouseEnter={e => e.currentTarget.style.borderColor = T.accent}
  onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
>
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.6 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7.1l-6.6 4.9C9.6 39.6 16.3 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.4-2.5 4.4-4.6 5.8l6.2 5.2C40.8 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-4z"/>
  </svg>
  Continue with Google
</button>


           

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <p style={{ color: T.textMuted, fontSize: 13 }}>
                Don't have an account?{' '}
                <span onClick={() => navigate('/register')} style={{ color: T.accent, cursor: 'pointer', fontWeight: 600 }}>Sign up</span>
              </p>
            </div>

            <div style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${T.border}`, textAlign: 'center' }}>
              <p style={{ color: T.textDim, fontSize: 11, lineHeight: 1.6 }}>
                By signing in, you agree to Mino's{' '}
                <span onClick={() => navigate('/terms')} style={{ color: T.textMuted, cursor: 'pointer' }}>Terms of Use</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}